/* assets/jv-faq.js  —  Just Vertical FAQ (drop-in)
 * ---------------------------------------------------------------------------
 * Self-contained: questions, styles and behaviour all live in this one file.
 * It renders into the FAQ markup that is ALREADY on /pages/faq — the existing
 * #faqTabs / #faqList / #faqSearchInput / #noResults elements — so nothing in
 * the theme editor has to change.
 *
 * INSTALL
 *   1. Save this file as  assets/jv-faq.js
 *   2. Add one line to  layout/theme.liquid  just before the closing body tag:
 *        <script src="{{ 'jv-faq.js' | asset_url }}" defer><\/script>
 *      (written with the escaped slash so this file stays safe to paste inline)
 *   3. Delete the old standalone FAQ .js file (see WHY below).
 *
 * WHY THE OLD FILE BROKE
 *   Its init ran `new FAQManager(faqData)` with no guard. On every page that
 *   did not contain #faqTabs, `this.tabsContainer` was null and the first line
 *   of renderTabs() — this.tabsContainer.appendChild(...) — threw. That single
 *   defect produced ~64% of the site's JavaScript errors. Everything here is
 *   wrapped in an IIFE and guarded, so it exits quietly instead of throwing,
 *   and it declares no globals (so it cannot collide with the old file either).
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* --------------------------------------------------------------- settings */
  // The old answers linked to "#ContactUsForm", an anchor that does not exist
  // on the FAQ page, so those links went nowhere. Change this if you add it.
  var CONTACT_URL = '/pages/contact-us-1';

  var ALL_LABEL = 'All Categories';
  var SEARCH_DEBOUNCE = 200;

  /* ------------------------------------------------------------------- data */
  var DATA = [
    { slug: 'general', name: 'General Information', questions: [
      { q: 'How do your systems work?',
        a: 'You can learn more about our systems by reading about <a href="/pages/our-technology">our technology</a> and <a href="/pages/our-process">our process</a>.' },
      { q: 'Where is Just Vertical located?',
        a: 'We are based in Toronto, Ontario, Canada, but we serve clients globally. Our headquarters and manufacturing facilities are equipped to handle projects of any scale, ensuring timely delivery and support wherever you are located.' },
      { q: 'Does Just Vertical construct farms internationally?',
        a: 'Yes, we construct farms internationally. Our team has experience in designing and installing hydroponic systems worldwide, adapting to various climatic and regulatory conditions to ensure successful implementations in different regions.' },
      { q: 'Is it possible to integrate these farms into existing agricultural setups?',
        a: 'Absolutely &mdash; our farms are designed to complement and integrate with existing agricultural operations. They can be used to diversify production, optimize space, and increase yield, enhancing overall farm efficiency and productivity.' },
      { q: 'How does hydroponic farming differ from traditional soil farming?',
        a: 'Hydroponic farming uses a water-based nutrient solution instead of soil, allowing for faster plant growth and higher yields. When plants are grown hydroponically, they also use up to 95% less water than traditional agriculture.' }
    ]},
    { slug: 'financial', name: 'Financial Information', questions: [
      { q: 'Is there funding available for a farm?',
        a: 'Yes! For technology such as ours, there are always new grants popping up.<br><br>There are also definitely financing options available for your green dreams. We partner with Harvest Today to provide financing options for new farms. To learn more, <a href="' + CONTACT_URL + '">get in touch</a> with a member of our team.' },
      { q: 'What is the expected ROI on a farm like this?',
        a: 'The expected ROI varies depending on the system size and crops grown. Generally, our systems offer a quick return on investment, with potential yields ranging from 5 to 12 tonnes annually, contributing to substantial cost savings and revenue generation. If you want to learn more, <a href="' + CONTACT_URL + '">book a call with one of our sales reps</a>.' },
      { q: 'What are the operational costs of running a container farm?',
        a: 'Operational costs include electricity, water, nutrients, and maintenance, which we can help you estimate based on your specific setup. On the low end, costs start at around $10 a month (ECO Living Green Wall).' }
    ]},
    { slug: 'installation', name: 'Installation and Setup', questions: [
      { q: 'How much work is required?',
        a: 'Our systems are built with ease of use in mind. There is obviously some work required, but our feedback from educators is that the hands-on use of the Just Vertical farm enriches the learning experience. Depending on size, the farms will take anywhere from 15 minutes to 1 hour of work for one person, per week.' },
      { q: 'How long does it take to set up a farm?',
        a: 'Farm installation time varies dependent on the type of farm. Our smaller scale farms can be up and operating in as little as 15 minutes. Our larger scale farms can take anywhere from a few days to several months depending on the size of the project.' },
      { q: 'Do you offer installation services?',
        a: 'Yes, we provide professional installation services for most of our systems. <a href="' + CONTACT_URL + '">Get in touch</a> so we can discuss your plans and explore the options you have available in terms of installation.' }
    ]},
    { slug: 'operation', name: 'Operation and Maintenance', questions: [
      { q: 'What kind of support is available?',
        a: 'We pride ourselves in being available. You can always reach our team at <a href="mailto:support@justvertical.com">support@justvertical.com</a>.' },
      { q: 'What kind of maintenance is required?',
        a: 'Regular maintenance includes monitoring water quality, nutrient levels, and pH balance. Routine checks on the lighting and HVAC systems are also necessary to ensure optimal growing conditions. We provide detailed guidelines and support to assist with maintenance.' },
      { q: 'Is it possible to automate the farming process?',
        a: 'Yes, our systems can be integrated with automation technologies (including IoT technologies) to streamline operations depending on your needs and requirements.' }
    ]},
    { slug: 'performance', name: 'Performance and Efficiency', questions: [
      { q: 'What is the expected lifespan of this system?',
        a: 'With proper maintenance, our hydroponic systems are designed to last over a decade. The durability of materials and components ensures long-term operation, providing a reliable and sustainable farming solution.' },
      { q: 'What is the average yield of a container farm?',
        a: 'You can expect to grow 10-12 tonnes per year of leafy greens.' },
      { q: 'What is the lifespan of the LED lights?',
        a: 'The LED lights in our systems have a lifespan of 50,000 hours.' }
    ]},
    { slug: 'products', name: 'Products and Customization', questions: [
      { q: 'What can I grow?',
        a: 'Let your imagination run wild. You can grow nearly every variety of herbs and leafy greens, and many varieties of flowering plants.' },
      { q: 'Can these systems be expanded or modified after installation?',
        a: 'Yes, our systems are modular and can be easily expanded or modified to meet growing needs. Whether you want to increase capacity or adapt to new crops, our flexible designs allow for seamless upgrades and adjustments.' },
      { q: 'Can I grow multiple crops at the same time?',
        a: 'Yes, our systems are designed to support the growth of multiple crops simultaneously by employing the use of several reservoirs and plant recipes at once.' }
    ]},
    { slug: 'space', name: 'Space and Location', questions: [
      { q: 'Can I customize my farm for my space?',
        a: 'Absolutely! We have a diverse set of farms available suited for every and any space. Because we own and design our technology, we can make the perfect farm for your needs.' },
      { q: 'How much space do I need?',
        a: 'Because we offer so many different options for different spaces, the minimum size you need is about 3 sq ft. We build farms up to and beyond 10,000 sq ft.' },
      { q: 'Can your systems be used in urban environments?',
        a: 'Absolutely! In fact, our farms are perfect for environments where agriculture may otherwise be inaccessible (like inner cities, for example). Our farms are also suitable for rural and remote locations.' }
    ]},
    { slug: 'technology', name: 'Technology and Innovation', questions: [
      { q: 'How much research has gone into your products?',
        a: 'Our organization was started as a spin-off of Masters research by two Masters of Science alumni &mdash; Kevin Jakiela and Conner Tidd. Research and development is behind everything we do.' },
      { q: 'Can I monitor my farm remotely?',
        a: 'Yes! As an add-on, we offer IoT technologies to help you monitor your farm remotely.' },
      { q: 'Can I use renewable energy sources to power the farm?',
        a: 'Absolutely! In fact, we often encourage our farmers to explore renewable energy sources to try and increase their reliance on traditional methods of food production and power.' }
    ]}
  ];

  /* ------------------------------------------------------------------ styles
   * Injected because the FAQ markup on the page has no stylesheet behind it —
   * which is why the "No questions found" message is currently always visible. */
  var CSS = [
    '.questions-section{display:flex;width:100%;margin:0 auto;padding:112px 64px;justify-content:center}',
    '.questions-container{display:flex;flex-direction:column;max-width:1440px;width:100%}',
    '.questions-header{text-align:center;padding:48px 20px;margin:0 auto}',
    '.questions-header h2{font-size:46px;margin-bottom:12px}',
    '.questions-search{margin-bottom:30px}',
    '.questions-search input{width:100%;padding:15px 20px;font-size:16px;border:2px solid #ddd;border-radius:8px}',
    '.questions-search input:focus{outline:none;border-color:#1c3a13}',
    '.questions-tabs{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:40px}',
    '.questions-tabs:empty{display:none;margin-bottom:0}',
    '.question-tab{padding:12px 24px;background-color:#fcfcf7;border:2px solid #1c3a13;border-radius:8px;',
    'cursor:pointer;font-size:14px;font-weight:600;color:#1c3a13;transition:background-color .3s ease,color .3s ease}',
    '.question-tab:hover{background-color:#F5F6F0}',
    '.question-tab:focus-visible{outline:2px solid #1c3a13;outline-offset:2px}',
    '.question-tab.active{background:#1c3a13;color:#fff;border-color:#1c3a13}',
    /* the original CSS styled .questions-list while the markup uses
       .questions-q-list, so the column layout and gap never applied */
    '.questions-q-list{display:flex;flex-direction:column;gap:12px}',
    '.question-item-row{background-color:#fcfcf7;border-radius:8px;overflow:hidden}',
    '.question-item-row.hidden{display:none}',
    '.question-toggle{width:100%;padding:20px;background:#fcfcf7;border:none;text-align:left;',
    'font-size:20px;cursor:pointer;display:flex;justify-content:space-between;align-items:center;',
    'gap:16px;font-family:inherit;transition:background .2s ease}',
    '.question-toggle:hover{background:#F5F6F0}',
    '.question-toggle:focus-visible{outline:2px solid #1c3a13;outline-offset:-2px}',
    '.question-toggle span{color:#1c3a13}',
    '.question-icon{font-size:20px;line-height:1;flex:none;color:#1c3a13;transition:transform .3s ease}',
    '.question-item-row.active .question-toggle,.question-item-row.active .question-answer{background:#F5F6F0}',
    '.question-item-row.active .question-icon{transform:rotate(45deg)}',
    /* 0fr -> 1fr expands to the real content height, so long answers can never
       be clipped the way a fixed max-height clipped them */
    '.question-answer{display:grid;grid-template-rows:0fr;opacity:0;',
    'transition:grid-template-rows .35s ease,opacity .25s ease}',
    '.question-answer-inner{overflow:hidden}',
    '.question-item-row.active .question-answer{grid-template-rows:1fr;opacity:1}',
    '.question-answer-inner>p{margin:0;padding:0 20px 20px;line-height:1.8}',
    '.question-answer a{color:#1c3a13;text-decoration:underline}',
    /* hidden by default so it cannot flash before JS runs */
    '.no-results{text-align:center;padding:40px;color:#6b6b6b;display:none}',
    '.no-results.show{display:block}',
    '@media (max-width:727px){',
    '.questions-section{padding:64px 16px}',
    '.questions-header{padding:24px 0}',
    '.questions-header h2{font-size:36px}',
    '.questions-tabs{gap:8px}',
    '.question-tab{padding:10px 16px;font-size:13px}',
    '.question-toggle{font-size:17px;padding:16px}',
    '.question-answer-inner>p{padding:0 16px 16px}}',
    '@media (prefers-reduced-motion:reduce){',
    '.question-answer,.question-icon,.question-tab,.question-toggle{transition:none}}'
  ].join('');

  /* ------------------------------------------------------------------ helpers */
  function injectCss() {
    if (document.getElementById('jv-faq-css')) return;
    var el = document.createElement('style');
    el.id = 'jv-faq-css';
    el.textContent = CSS;
    (document.head || document.documentElement).appendChild(el);
  }

  function textOf(html) {
    var t = document.createElement('div');
    t.innerHTML = html;
    return (t.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function injectJsonLd() {
    if (document.getElementById('jv-faq-jsonld')) return;
    var entities = [];
    DATA.forEach(function (cat) {
      cat.questions.forEach(function (item) {
        entities.push({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: textOf(item.a) }
        });
      });
    });
    var el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'jv-faq-jsonld';
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: entities
    });
    (document.head || document.documentElement).appendChild(el);
  }

  /* --------------------------------------------------------------------- init */
  function initFaq() {
    var list = document.getElementById('faqList');
    var tabsWrap = document.getElementById('faqTabs');
    var searchInput = document.getElementById('faqSearchInput');
    var noResults = document.getElementById('noResults');

    // THE GUARD. No FAQ markup on this page? Do nothing at all.
    if (!list) return;
    if (list.getAttribute('data-jv-faq-ready') === 'true') return;
    list.setAttribute('data-jv-faq-ready', 'true');

    injectCss();
    injectJsonLd();

    var activeCategory = 'all';
    var searchTerm = '';
    var rows = [];

    /* ---- render the questions ---- */
    list.innerHTML = '';
    DATA.forEach(function (cat) {
      cat.questions.forEach(function (item, i) {
        var id = 'jv-faq-' + cat.slug + '-' + i;

        var row = document.createElement('div');
        row.className = 'question-item-row';
        row.setAttribute('data-category', cat.slug);
        row.setAttribute('data-search', (item.q + ' ' + textOf(item.a)).toLowerCase());

        var btn = document.createElement('button');
        btn.className = 'question-toggle';
        btn.type = 'button';
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('aria-controls', id);
        var label = document.createElement('span');
        label.textContent = item.q;
        var icon = document.createElement('span');
        icon.className = 'question-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '+';
        btn.appendChild(label);
        btn.appendChild(icon);

        var panel = document.createElement('div');
        panel.className = 'question-answer';
        panel.id = id;
        var inner = document.createElement('div');
        inner.className = 'question-answer-inner';
        var p = document.createElement('p');
        p.innerHTML = item.a;   // answers intentionally contain links / <br>
        inner.appendChild(p);
        panel.appendChild(inner);

        row.appendChild(btn);
        row.appendChild(panel);
        list.appendChild(row);
        rows.push(row);

        btn.addEventListener('click', function () {
          var willOpen = !row.classList.contains('active');
          rows.forEach(function (other) {
            if (other === row) return;
            other.classList.remove('active');
            var ob = other.querySelector('.question-toggle');
            if (ob) ob.setAttribute('aria-expanded', 'false');
          });
          row.classList.toggle('active', willOpen);
          btn.setAttribute('aria-expanded', String(willOpen));
        });
      });
    });

    /* ---- category buttons ---- */
    function makeTab(slug, label, isActive) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'question-tab' + (isActive ? ' active' : '');
      b.textContent = label;
      b.setAttribute('data-category', slug);
      b.setAttribute('aria-pressed', String(!!isActive));
      b.addEventListener('click', function () {
        activeCategory = slug;
        var all = tabsWrap.querySelectorAll('.question-tab');
        Array.prototype.forEach.call(all, function (t) {
          var on = t.getAttribute('data-category') === slug;
          t.classList.toggle('active', on);
          t.setAttribute('aria-pressed', String(on));
        });
        applyFilters();
      });
      return b;
    }

    if (tabsWrap) {
      tabsWrap.innerHTML = '';
      tabsWrap.appendChild(makeTab('all', ALL_LABEL, true));
      DATA.forEach(function (cat) {
        if (cat.questions && cat.questions.length) {
          tabsWrap.appendChild(makeTab(cat.slug, cat.name, false));
        }
      });
    }

    /* ---- search ---- */
    if (searchInput) {
      var timer;
      searchInput.addEventListener('input', function (e) {
        var value = e.target.value;
        clearTimeout(timer);
        timer = setTimeout(function () {
          searchTerm = value.toLowerCase().trim();
          applyFilters();
        }, SEARCH_DEBOUNCE);
      });
    }

    /* ---- filtering ---- */
    function applyFilters() {
      var visible = 0;
      rows.forEach(function (row) {
        var okCat = activeCategory === 'all' ||
                    row.getAttribute('data-category') === activeCategory;
        // searches the answer text too, so a word that only appears in an
        // answer still finds its question
        var okTerm = !searchTerm ||
                     (row.getAttribute('data-search') || '').indexOf(searchTerm) !== -1;
        var show = okCat && okTerm;

        row.classList.toggle('hidden', !show);
        if (show) {
          visible++;
        } else {
          row.classList.remove('active');
          var b = row.querySelector('.question-toggle');
          if (b) b.setAttribute('aria-expanded', 'false');
        }
      });

      if (noResults) {
        noResults.classList.toggle('show', visible === 0);
        noResults.setAttribute('role', 'status');
        noResults.setAttribute('aria-live', 'polite');
      }
    }

    applyFilters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFaq);
  } else {
    initFaq();
  }

  // Theme editor / dynamic sections: re-run if the markup is re-rendered.
  document.addEventListener('shopify:section:load', initFaq);
})();